import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { has, isArray } from "lodash";
import moment from "moment";
import Typography from "@material-ui/core/Typography";
import Container from "@material-ui/core/Container";
import CircularProgress from "@material-ui/core/CircularProgress";
import { makeStyles } from "@material-ui/core/styles";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: theme.spacing(2),
    padding: theme.spacing(3),
    textAlign: "center",
  },
}));

/** Survives remounts — avoids double-consume of a one-time code. */
const inflightByCode = new Map();

function safeRedirectPath(raw) {
  const path = String(raw || "/tickets").trim() || "/tickets";
  if (!path.startsWith("/") || path.startsWith("//") || path.includes("://")) {
    return "/tickets";
  }
  return path;
}

function applySessionLocalStorage(data) {
  const company = data?.user?.company;

  if (
    has(company, "companieSettings") &&
    isArray(company.companieSettings[0])
  ) {
    const campaigns = company.companieSettings[0].find(
      (s) => s.key === "campaignsEnabled"
    );
    if (campaigns && campaigns.value === "true") {
      localStorage.setItem("cshow", null);
    }
    const sign = company.companieSettings[0].find(
      (s) => s.key === "sendSignMessage"
    );
    if (sign && sign.value === "enabled") {
      localStorage.setItem("sendSignMessage", true);
    }
  }

  if (data?.user?.profileImage) {
    localStorage.setItem("profileImage", data.user.profileImage);
  }

  moment.locale("pt-br");
  let dueDate;
  if (data?.user?.company?.id === 1) {
    dueDate = "2999-12-31T00:00:00.000Z";
  } else {
    dueDate = data?.user?.company?.dueDate;
  }

  const vencimento = dueDate ? moment(dueDate).format("DD/MM/yyyy") : "";
  const hojeInicio = moment().startOf("day");
  const vencimentoInicio = dueDate ? moment(dueDate).startOf("day") : null;
  const before =
    !vencimentoInicio || hojeInicio.isSameOrBefore(vencimentoInicio, "day");

  localStorage.setItem("token", JSON.stringify(data.token));
  if (vencimento) {
    localStorage.setItem("companyDueDate", vencimento);
  }
  api.defaults.headers.Authorization = `Bearer ${data.token}`;

  return before;
}

async function consumeCode(code) {
  if (inflightByCode.has(code)) {
    return inflightByCode.get(code);
  }
  const promise = api
    .post("/auth/heidi-handoff/consume", { code })
    .then((res) => res.data)
    .finally(() => {
      // keep briefly so a remount can reuse; drop after settle
      setTimeout(() => inflightByCode.delete(code), 5000);
    });
  inflightByCode.set(code, promise);
  return promise;
}

/**
 * Heidi Sales → Messager one-time SSO handoff.
 * Expects ?code=…&redirect=/tickets
 */
const SsoHandoff = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const [status, setStatus] = useState("A entrar no atendimento…");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = (params.get("code") || "").trim();
    const redirectPath = safeRedirectPath(params.get("redirect"));

    if (!code) {
      setStatus("Código em falta.");
      toastError("Código de acesso inválido ou em falta.");
      history.replace("/login");
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const data = await consumeCode(code);
        if (cancelled) return;

        const before = applySessionLocalStorage(data);
        setStatus("Sessão pronta. A redirecionar…");

        // Full reload so AuthContext picks up token/cookie like a fresh login
        const target = before ? redirectPath : "/financeiro-aberto";
        window.location.replace(target);
      } catch (err) {
        if (cancelled) return;
        setStatus("Falha no acesso automático.");
        toastError(err);
        setTimeout(() => history.replace("/login"), 1200);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [history, location.search]);

  return (
    <Container className={classes.root} maxWidth="sm">
      <CircularProgress />
      <Typography variant="h6">{status}</Typography>
    </Container>
  );
};

export default SsoHandoff;
